import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeleteAccountDialog } from "@/components/DeleteAccountDialog";
import DataExportCard from "@/components/DataExportCard";

function Field({ label, value, testId }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="break-all text-sm font-medium" data-testid={testId}>{value || "–"}</div>
    </div>
  );
}

export const SelfDeleteButton = ({ variant = "destructive" }) => {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const onConfirm = async (body) => {
    await api.post("/auth/delete-account", body);
    toast.success("Ihr Konto und Ihre personenbezogenen Daten wurden gelöscht.");
    nav("/", { replace: true });
    await refreshMe();
  };
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)} data-testid="delete-account-btn">Konto löschen</Button>
      <DeleteAccountDialog
        open={open}
        onOpenChange={setOpen}
        title="Konto endgültig löschen"
        description="Ihr Konto, Ihre Kontaktdaten, Adresse, Nachrichten, Messwerte und Erinnerungen werden unwiderruflich gelöscht. Bisherige Buchungen bleiben nur anonymisiert (Datum und Menge) für die Kontingent-Statistik der Gemeinde erhalten, offene künftige Buchungen werden storniert. Ihre Gemeinde wird über die Abmeldung informiert."
        passwordLabel={user?.has_password ? "Ihr Passwort" : "Ihre E-Mail-Adresse (Google-Konto)"}
        onConfirm={onConfirm}
      />
    </>
  );
};

export default function ProfilePanel() {
  const { user } = useAuth();
  return (
    <div className="space-y-4" data-testid="profile-panel">
      <Card>
        <CardContent className="space-y-4 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold"><UserRound className="h-5 w-5" /> Meine Daten</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" value={user?.name} testId="profile-name" />
            <Field label="E-Mail" value={user?.email} testId="profile-email" />
            <Field label="Adresse" value={user?.address} testId="profile-address" />
            {user?.role === "pool_owner" && <Field label="Pool" value={user?.pool_area ? `${user.pool_area} m² · ${user.pool_depth ?? "–"} m` : ""} testId="profile-pool" />}
          </div>
        </CardContent>
      </Card>
      <DataExportCard />
      {user?.role === "pool_owner" && (
      <Card className="border-destructive/40">
        <CardContent className="space-y-3 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-destructive"><ShieldAlert className="h-5 w-5" /> Konto löschen (Recht auf Löschung)</h2>
          <p className="text-sm text-muted-foreground">
            Nach Art. 17 DSGVO können Sie Ihr Konto jederzeit selbst löschen. Personenbezogene Daten werden sofort entfernt;
            Buchungsmengen bleiben ohne Personenbezug für die Wasserplanung der Gemeinde erhalten.
          </p>
          <SelfDeleteButton />
        </CardContent>
      </Card>
      )}
    </div>
  );
}
